/**
 * Pre-order line reconciliation.
 *
 * The cart chip and notice are rendered from live variant data, so what the
 * shopper sees is always correct. The saved line property is what carries the
 * promise into checkout, the confirmation email and the Admin order, and it
 * can drift from reality in two directions. The AJAX add paths (cart upsell,
 * shipping-bar gap, quiz, shoppable video) post a bare variant id, so the
 * property is missing. And when stock lands while an item sits in a cart, the
 * property is left over, so checkout would repeat a wait we have already
 * beaten. Liquid marks both cases in an inert JSON payload on every cart
 * render; this adds or strips the property to match.
 *
 * Deliberately conservative: one attempt per line key, a hard ceiling per page
 * load, and every failure is swallowed. A pre-order line that keeps its
 * property is a nicety; a cart that mutates in a loop is a disaster.
 */
(function () {
  'use strict';

  var MAX_CALLS = 5;
  var attempted = Object.create(null);
  var calls = 0;
  var running = false;

  function rootUrl() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.slice(-1) === '/' ? r : r + '/';
  }

  function payloads() {
    var out = [];
    var nodes = document.querySelectorAll('[data-preorder-reconcile]');
    for (var i = 0; i < nodes.length; i++) {
      try {
        var parsed = JSON.parse(nodes[i].textContent || '[]');
        if (Object.prototype.toString.call(parsed) === '[object Array]') {
          for (var j = 0; j < parsed.length; j++) out.push(parsed[j]);
        }
      } catch (e) { /* malformed payload is never worth breaking the cart over */ }
    }
    return out;
  }

  function sectionsToRender() {
    var host = document.querySelector('cart-drawer') || document.querySelector('cart-notification');
    if (host && typeof host.getSectionsToRender === 'function') {
      try {
        return {
          host: host,
          ids: host.getSectionsToRender().map(function (s) { return s.id; })
        };
      } catch (e) { /* fall through */ }
    }
    if (document.getElementById('main-cart-items')) {
      return { host: null, ids: ['main-cart-items', 'main-cart-footer'] };
    }
    return { host: null, ids: [] };
  }

  function innerHTMLOf(markup, selector) {
    var doc = new DOMParser().parseFromString(markup, 'text/html');
    var found = doc.querySelector(selector);
    return found ? found.innerHTML : null;
  }

  function repaintCartPage(sections) {
    ['main-cart-items', 'main-cart-footer'].forEach(function (id) {
      var el = document.getElementById(id);
      if (!el || !sections || !sections[id]) return;
      var html = innerHTMLOf(sections[id], '.js-contents');
      if (html !== null) {
        var target = el.querySelector('.js-contents');
        if (target) target.innerHTML = html;
      }
    });
  }

  function reconcile() {
    if (running || calls >= MAX_CALLS) return;
    var pending = payloads().filter(function (p) {
      if (!p || !p.key || attempted[p.key]) return false;
      return p.action === 'remove' ? true : !!p.window;
    });
    if (!pending.length) return;

    var line = pending[0];
    attempted[line.key] = true;
    running = true;
    calls += 1;

    var target = sectionsToRender();
    var body = {
      id: line.key,
      quantity: line.qty > 0 ? line.qty : 1,
      // 'remove' sends the line's other properties back without the stale
      // pre-order one, so nothing else on the line is lost.
      properties: line.action === 'remove'
        ? (line.props || {})
        : { 'Pre-order': line.window }
    };
    if (target.ids.length) {
      body.sections = target.ids.join(',');
      body.sections_url = window.location.pathname;
    }

    fetch(rootUrl() + 'cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body)
    })
      .then(function (r) { return r.ok ? r.json() : Promise.reject(r); })
      .then(function (data) {
        if (target.host && typeof target.host.renderContents === 'function' && data && data.sections) {
          target.host.renderContents(data);
        } else if (data && data.sections) {
          repaintCartPage(data.sections);
        }
        try {
          if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
            publish(PUB_SUB_EVENTS.cartUpdate, { source: 'preorder-reconcile', cartData: data });
          }
        } catch (e) { /* pubsub is optional */ }
      })
      .catch(function () { /* leave the line as-is; the cart still displays correctly */ })
      .then(function () {
        running = false;
        window.setTimeout(reconcile, 60);
      });
  }

  /**
   * The Loop Subscriptions widget rewrites the submit label after load
   * ("Add subscription to cart", "Add to cart"), which wiped the pre-order
   * wording off the primary CTA on the SKUs Loop is configured for.
   * Re-assert it, but only when the label has lost it, so this settles
   * immediately instead of ping-ponging with Loop.
   */
  function guardButtonLabel() {
    var form = document.querySelector('product-form.product-form[data-preorder="true"]');
    if (!form) return;
    var btn = form.querySelector('.product-form__submit');
    if (!btn) return;
    var base = form.getAttribute('data-preorder-label') || 'Pre-order now';
    var writes = 0;

    function apply() {
      var span = btn.querySelector('span');
      if (!span || writes > 50) return;
      var text = (span.textContent || '').trim();
      if (!text || /^pre-order/i.test(text)) return;
      writes += 1;
      span.textContent = /subscription/i.test(text) ? 'Pre-order subscription' : base;
    }

    apply();
    new MutationObserver(apply).observe(btn, {
      childList: true, subtree: true, characterData: true
    });
  }

  var queued = null;
  function schedule() {
    window.clearTimeout(queued);
    queued = window.setTimeout(reconcile, 180);
  }

  function watch() {
    schedule();
    guardButtonLabel();
    var observer = new MutationObserver(schedule);
    ['cart-drawer', 'cart-notification', '#main-cart-items'].forEach(function (sel) {
      var el = document.querySelector(sel);
      if (el) observer.observe(el, { childList: true, subtree: true });
    });
    document.addEventListener('cart:refresh', schedule);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', watch);
  } else {
    watch();
  }
})();
